/**
 * ResultPanel — full-page result rendering for the cold-start tool.
 *
 * Sections:
 *   1. Verdict hero  — winner, savings, action prompt
 *   2. Side-by-side regime totals
 *   3. What-if sliders — drag to recompute live
 *   4. Top opportunities — ranked by ₹ impact at marginal rate
 *   5. Advance-tax callout (only if liability triggers Sec 208)
 *   6. Per-regime breakdown rows
 *   7. Disclaimer + restart CTA
 */
import * as React from 'react';
import { Calendar, RefreshCw, Sparkles, TrendingDown } from 'lucide-react';
import type { ProfileInput, RegimeResult, AdvanceTaxSchedule, Opportunity, ProfileNumericKey } from '../types';
import { PALETTE_FOREST, PALETTE_AMBER, PALETTE_MINT } from '../tokens';
import { fmt, fmtFull, fmtPct } from '../format';

interface ResultPanelProps {
  profile:           ProfileInput;
  oldResult:         RegimeResult;
  newResult:         RegimeResult;
  opportunities:     Opportunity[];
  advanceTax:        AdvanceTaxSchedule | null;
  onWhatIfChange:    <K extends ProfileNumericKey>(key: K, value: number) => void;
  onRestart:         () => void;
}

interface WhatIfLever {
  field:  ProfileNumericKey;
  label:  string;
  sec:    string;
  min:    number;
  max:    number;
  step:   number;
  isPct?: boolean;
}

const WHATIF_LEVERS: WhatIfLever[] = [
  { field: 's80c',             label: 'Section 80C investment',     sec: 'S.123',     min: 0, max: 150000, step: 5000 },
  { field: 'nps80ccd1b',       label: 'NPS Tier-1 (own)',           sec: 'S.124(3)',  min: 0, max: 50000,  step: 5000 },
  { field: 'homeLoanInterest', label: 'Home loan interest',         sec: 'S.22(2)(a)', min: 0, max: 200000, step: 5000 },
  { field: 'employerNpsPct',   label: 'Employer NPS — % of basic',  sec: 'S.124(1)',  min: 0, max: 14,     step: 0.5, isPct: true },
  { field: 's80dParents',      label: 'Health insurance — parents', sec: 'S.126',     min: 0, max: 50000,  step: 5000 },
];

export function ResultPanel({
  profile, oldResult, newResult, opportunities, advanceTax, onWhatIfChange, onRestart,
}: ResultPanelProps) {
  const oldTax  = oldResult.total;
  const newTax  = newResult.total;
  const diff    = Math.abs(oldTax - newTax);
  const close   = diff < 10000;
  const winner: 'old' | 'new' | 'close' =
    close ? 'close' : oldTax < newTax ? 'old' : 'new';

  return (
    <div className="space-y-5 animate-slide-up-fade">

      {/* ── Verdict hero ───────────────────────────────────────────── */}
      <VerdictHero winner={winner} diff={diff} oldResult={oldResult} />

      {/* ── Side-by-side totals ───────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3">
        <RegimeTile label="Old regime tax"  total={oldTax}  isWinner={winner === 'old'} />
        <RegimeTile label="New regime tax"  total={newTax}  isWinner={winner === 'new'} />
      </div>

      {/* ── What-if sliders ───────────────────────────────────────── */}
      <section className="rounded-2xl border border-border bg-background p-5 sm:p-6">
        <div className="flex items-start gap-3 mb-4">
          <span
            className="h-9 w-9 rounded-lg grid place-items-center shrink-0"
            style={{ backgroundColor: PALETTE_MINT }}
          >
            <Sparkles className="h-4 w-4" style={{ color: PALETTE_FOREST }} aria-hidden />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: PALETTE_AMBER }}>
              What-if explorer
            </p>
            <h3 className="font-display text-base sm:text-lg font-semibold text-foreground tracking-tight">
              Adjust the highest-leverage levers — numbers update live.
            </h3>
          </div>
        </div>
        <div className="space-y-3">
          {WHATIF_LEVERS.map((l) => (
            <WhatIfRow
              key={l.field}
              lever={l}
              value={profile[l.field] as number}
              onChange={(v) => onWhatIfChange(l.field, v)}
            />
          ))}
        </div>
      </section>

      {/* ── Top opportunities ─────────────────────────────────────── */}
      {opportunities.length > 0 && (
        <section className="rounded-2xl border border-border bg-background p-5 sm:p-6">
          <div className="flex items-start gap-3 mb-4">
            <span
              className="h-9 w-9 rounded-lg grid place-items-center shrink-0"
              style={{ backgroundColor: `${PALETTE_AMBER}18` }}
            >
              <TrendingDown className="h-4 w-4" style={{ color: PALETTE_AMBER }} aria-hidden />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: PALETTE_AMBER }}>
                Top opportunities
              </p>
              <h3 className="font-display text-base sm:text-lg font-semibold text-foreground tracking-tight">
                Ranked by rupee impact at your marginal rate.
              </h3>
            </div>
          </div>
          <ol className="space-y-3">
            {opportunities.map((o) => <OpportunityRow key={o.rank} opportunity={o} />)}
          </ol>
        </section>
      )}

      {/* ── Advance-tax callout (conditional) ─────────────────────── */}
      {advanceTax && <AdvanceTaxCallout schedule={advanceTax} />}

      {/* ── Breakdown ─────────────────────────────────────────────── */}
      <BreakdownSection result={oldResult} regime="old" profile={profile} />
      <BreakdownSection result={newResult} regime="new" profile={profile} />

      {/* ── Disclaimer + restart ─────────────────────────────────── */}
      <div className="rounded-2xl border border-border bg-background/60 p-4 text-[11px] text-muted-foreground leading-relaxed">
        <p className="mb-2">
          <span className="font-semibold text-foreground">Heads up — regime choice timing.</span>{' '}
          Telling your employer which regime to use is provisional and only affects TDS. Your final
          regime choice is locked when you file your ITR. Salaried taxpayers without business income
          can switch regimes year-on-year; business / professional taxpayers face lock-in restrictions
          (Section 202, IT Act 2025).
        </p>
        <p>
          <span className="font-semibold text-foreground">Indicative estimate.</span>{' '}
          Rental income, business / professional income, and full perquisite valuation rules are not
          yet modelled. Capital gains use post-23-Jul-2024 rates (12.5% LTCG / 20% STCG equity).
          Senior-citizen old-regime slabs are historical values pending FY 26-27 verification. HRA
          metro list now includes Bengaluru, Hyderabad, Pune and Ahmedabad per Rule 279 of the Draft
          Income Tax Rules 2026 — pending final notification.
        </p>
      </div>

      <button
        type="button"
        onClick={onRestart}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold border border-border text-foreground bg-background hover:bg-muted transition-colors"
      >
        <RefreshCw className="h-3.5 w-3.5" aria-hidden />
        Recalculate with different inputs
      </button>
    </div>
  );
}

// ── Verdict hero ───────────────────────────────────────────────────────────

function VerdictHero({
  winner, diff, oldResult,
}: {
  winner:    'old' | 'new' | 'close';
  diff:      number;
  oldResult: RegimeResult;
}) {
  const isClose = winner === 'close';
  const eyebrow = isClose
    ? 'Too close to call'
    : winner === 'old' ? 'Recommendation — Old regime' : 'Recommendation — New regime';
  const title = isClose
    ? 'Difference is under ₹10,000 — either regime works.'
    : winner === 'old'
      ? 'Old regime saves you more this year.'
      : 'New regime saves you more this year.';
  const note = isClose
    ? 'Choose new regime for simpler compliance, or old if your deductions will grow this year.'
    : winner === 'old'
      ? `Your deductions of ${fmt(oldResult.deductions.oldOnly)} (incl. HRA exemption ${fmt(oldResult.deductions.hraExemption)}) cross the break-even point. Declare old regime and keep investment proofs ready.`
      : `Old-regime deductions of ${fmt(oldResult.deductions.oldOnly)} fall short of the break-even for your income. Declare new regime to your employer via Form 12BB.`;

  return (
    <div
      className="rounded-2xl overflow-hidden relative animate-slide-up-fade"
      style={{ backgroundColor: PALETTE_FOREST }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-20 -right-20 h-64 w-64 rounded-full blur-3xl opacity-25 animate-float-slow"
        style={{ backgroundColor: PALETTE_AMBER }}
      />
      <div className="relative p-6 sm:p-8">
        <p
          className="text-[10px] font-bold uppercase tracking-widest mb-3"
          style={{ color: PALETTE_AMBER }}
        >
          {eyebrow}
        </p>
        <h2
          className="font-display font-semibold text-2xl sm:text-3xl leading-tight tracking-tight"
          style={{ color: 'hsl(var(--palette-cream, 40 100% 98%))' }}
        >
          {title}
        </h2>
        {!isClose && (
          <p
            className="font-mono tabular-nums mt-3 text-xl sm:text-2xl font-bold"
            style={{ color: PALETTE_AMBER }}
          >
            {fmtFull(diff)} <span className="text-sm font-medium opacity-80">lower tax</span>
          </p>
        )}
        {isClose && (
          <p className="font-mono tabular-nums mt-3 text-base sm:text-lg" style={{ color: PALETTE_AMBER }}>
            Δ {fmtFull(diff)}
          </p>
        )}
        <p
          className="text-xs sm:text-sm mt-3 leading-relaxed max-w-2xl"
          style={{ color: 'hsl(var(--palette-cream, 40 100% 98%) / 0.78)' }}
        >
          {note}
        </p>
      </div>
    </div>
  );
}

// ── Side-by-side regime tile ───────────────────────────────────────────────

function RegimeTile({ label, total, isWinner }: { label: string; total: number; isWinner: boolean }) {
  return (
    <div
      className="rounded-2xl border bg-background p-4 transition-all"
      style={{
        borderColor:     isWinner ? PALETTE_FOREST : 'hsl(var(--border))',
        backgroundColor: isWinner ? `${PALETTE_FOREST}06` : undefined,
      }}
    >
      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">
        {label}
      </p>
      <p
        className="font-mono tabular-nums text-xl sm:text-2xl font-semibold"
        style={{ color: isWinner ? PALETTE_FOREST : 'hsl(var(--foreground))' }}
      >
        {fmtFull(total)}
      </p>
      <p className="text-[10px] text-muted-foreground mt-0.5">incl. surcharge + 4% cess</p>
    </div>
  );
}

// ── What-if slider row ─────────────────────────────────────────────────────

function WhatIfRow({
  lever, value, onChange,
}: {
  lever:    WhatIfLever;
  value:    number;
  onChange: (v: number) => void;
}) {
  const display = lever.isPct ? `${value}%` : fmtFull(value);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-medium text-foreground flex items-center gap-1.5 min-w-0">
          <span className="truncate">{lever.label}</span>
          <span
            className="text-[9px] font-mono font-bold uppercase tracking-widest px-1 py-0.5 rounded shrink-0"
            style={{ backgroundColor: `${PALETTE_FOREST}10`, color: PALETTE_FOREST }}
          >
            {lever.sec}
          </span>
        </span>
        <span className="font-mono tabular-nums text-xs font-semibold" style={{ color: PALETTE_FOREST }}>
          {display}
        </span>
      </div>
      <input
        type="range"
        min={lever.min}
        max={lever.max}
        step={lever.step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-current"
        style={{ color: PALETTE_AMBER }}
      />
    </div>
  );
}

// ── Opportunity row ────────────────────────────────────────────────────────

function OpportunityRow({ opportunity: o }: { opportunity: Opportunity }) {
  return (
    <li className="flex items-start gap-3 rounded-xl border border-border bg-background/60 p-3.5">
      <span
        className="font-mono text-[10px] font-bold tabular-nums h-7 w-7 rounded-md grid place-items-center shrink-0"
        style={{ backgroundColor: `${PALETTE_FOREST}10`, color: PALETTE_FOREST }}
      >
        {String(o.rank).padStart(2, '0')}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground leading-snug">{o.title}</p>
        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
          <span
            className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded"
            style={{
              backgroundColor: o.ease === 'easy' ? `${PALETTE_FOREST}12` : `${PALETTE_AMBER}18`,
              color:           o.ease === 'easy' ? PALETTE_FOREST          : PALETTE_AMBER,
            }}
          >
            {o.ease === 'easy' ? 'Easy win' : 'Structural'}
          </span>
          <span
            className="text-[9px] font-mono font-bold uppercase tracking-widest px-1 py-0.5 rounded"
            style={{ backgroundColor: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}
          >
            {o.sec}
          </span>
          <span className="text-[10.5px] text-muted-foreground leading-snug">{o.caveat}</span>
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className="font-mono tabular-nums text-sm font-bold" style={{ color: PALETTE_FOREST }}>
          + {fmt(Math.round(o.impact))}
        </p>
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground">est. saving</p>
      </div>
    </li>
  );
}

// ── Advance-tax callout ────────────────────────────────────────────────────

function AdvanceTaxCallout({ schedule }: { schedule: AdvanceTaxSchedule }) {
  return (
    <section
      className="rounded-2xl p-5 sm:p-6"
      style={{ backgroundColor: `${PALETTE_AMBER}10`, borderLeft: `4px solid ${PALETTE_AMBER}` }}
    >
      <div className="flex items-start gap-3 mb-4">
        <span
          className="h-9 w-9 rounded-lg grid place-items-center shrink-0"
          style={{ backgroundColor: `${PALETTE_AMBER}25` }}
        >
          <Calendar className="h-4 w-4" style={{ color: PALETTE_AMBER }} aria-hidden />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: PALETTE_AMBER }}>
            Advance tax obligation flagged
          </p>
          <h3 className="font-display text-base sm:text-lg font-semibold text-foreground tracking-tight">
            Estimated <span className="font-mono tabular-nums">{fmtFull(schedule.total)}</span> in
            advance tax due across FY 2026-27.
          </h3>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {(['q1', 'q2', 'q3', 'q4'] as const).map((q, i) => {
          const data = schedule[q];
          return (
            <div key={q} className="rounded-xl bg-background border border-border p-3">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                Q{i + 1} · {data.dueDate}
              </p>
              <p className="font-mono tabular-nums font-semibold text-sm text-foreground mt-1">
                {fmtFull(data.amount)}
              </p>
              <p className="text-[10px] text-muted-foreground mt-0.5">cum. {fmtFull(data.cumulative)}</p>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
        Advance tax kicks in once estimated annual liability exceeds ₹10,000 (Section 208). Salaried
        employees with adequate TDS may not need to pay separately — check Form 26AS each quarter.
      </p>
    </section>
  );
}

// ── Breakdown rows ─────────────────────────────────────────────────────────

function BreakdownSection({
  result, regime, profile,
}: {
  result:  RegimeResult;
  regime:  'old' | 'new';
  profile: ProfileInput;
}) {
  const rows = buildBreakdownRows(result, regime, profile);
  return (
    <section className="rounded-2xl border border-border bg-background overflow-hidden">
      <div
        className="flex items-center gap-2 px-5 py-3 border-b border-border"
        style={{ backgroundColor: regime === 'old' ? `${PALETTE_FOREST}08` : `${PALETTE_AMBER}10` }}
      >
        <span
          className="h-2 w-2 rounded-full shrink-0"
          style={{ backgroundColor: regime === 'old' ? PALETTE_FOREST : PALETTE_AMBER }}
        />
        <h4 className="font-display text-sm font-semibold text-foreground tracking-tight">
          {regime === 'old' ? 'Old regime' : 'New regime'} — how we computed
        </h4>
      </div>
      <ul className="divide-y divide-border">
        {rows.map((row, i) => (
          <li key={i} className="flex items-center justify-between gap-3 px-5 py-2.5">
            <span className="text-xs text-muted-foreground">{row.label}</span>
            <span
              className={`font-mono tabular-nums text-xs ${row.deduct ? 'text-destructive' : 'text-foreground font-semibold'}`}
            >
              {row.value}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

interface BreakdownRow { label: string; value: string; deduct: boolean }

function buildBreakdownRows(r: RegimeResult, regime: 'old' | 'new', profile: ProfileInput): BreakdownRow[] {
  const rows: BreakdownRow[] = [];
  rows.push({ label: 'Gross salary',                    value: fmtFull(profile.gross),               deduct: false });
  rows.push({ label: 'Employer NPS + perks (S.124(1))', value: '−' + fmtFull(r.deductions.bothPerks), deduct: true  });
  rows.push({ label: 'Standard deduction (S.19)',       value: '−' + fmtFull(r.deductions.stdDed),    deduct: true  });

  if (regime === 'old') {
    if (r.deductions.hraExemption > 0)
      rows.push({ label: 'HRA exemption (actual rent)', value: '−' + fmtFull(r.deductions.hraExemption), deduct: true });
    const otherOldOnly = r.deductions.oldOnly - r.deductions.hraExemption;
    if (otherOldOnly > 0)
      rows.push({ label: '80C / NPS / 80D / home loan / etc.', value: '−' + fmtFull(otherOldOnly), deduct: true });
  } else {
    rows.push({ label: 'All other deductions', value: 'not available in new regime', deduct: false });
  }

  rows.push({ label: 'Net taxable income (slab portion)', value: fmtFull(r.taxableIncome), deduct: false });
  rows.push({ label: 'Slab tax',                          value: fmtFull(r.slabTax),       deduct: false });
  if (r.rebate > 0)    rows.push({ label: 'Rebate u/s 87A', value: '−' + fmtFull(r.rebate), deduct: true });
  if (r.surcharge > 0) {
    rows.push({
      label: `Surcharge (${fmtPct(r.surchargeRate)}${r.surchargeReliefApplied ? ', marginal relief applied' : ''})`,
      value: '+' + fmtFull(r.surcharge), deduct: false,
    });
  }

  const cg = r.capitalGains;
  if (cg && cg.total > 0) {
    if (cg.ltcgEqTax > 0)   rows.push({ label: 'LTCG equity tax (12.5%) S.112A',  value: '+' + fmtFull(cg.ltcgEqTax),   deduct: false });
    if (cg.stcgEqTax > 0)   rows.push({ label: 'STCG equity tax (20%) S.111A',    value: '+' + fmtFull(cg.stcgEqTax),   deduct: false });
    if (cg.ltcgDebtTax > 0) rows.push({ label: 'LTCG debt tax (12.5%) S.112',     value: '+' + fmtFull(cg.ltcgDebtTax), deduct: false });
    if (cg.ltcgReTax > 0)   rows.push({ label: 'LTCG real-estate tax (12.5%) S.112', value: '+' + fmtFull(cg.ltcgReTax), deduct: false });
    if (cg.surcharge > 0)
      rows.push({ label: `Surcharge on CG tax (${fmtPct(cg.surchargeRate)}, capped at 15%)`, value: '+' + fmtFull(cg.surcharge), deduct: false });
  }

  rows.push({ label: 'Health & Education cess (4%)', value: '+' + fmtFull(r.cess),  deduct: false });
  rows.push({ label: 'Total tax payable',            value: fmtFull(r.total),       deduct: false });
  return rows;
}

